/**
 * When adding a new component type that needs file details on fetch, you must also add it to the backend checks that injects those.
 * Related checks in file https://github.com/PitcherAG/ng-rest-api/blob/main/pitcher/serializers/canvasserializers.py:
 * - https://github.com/PitcherAG/ng-rest-api/blob/main/pitcher/serializers/canvasserializers.py#L55
 * - https://github.com/PitcherAG/ng-rest-api/blob/main/pitcher/serializers/canvasserializers.py#L118
 * - https://github.com/PitcherAG/ng-rest-api/blob/main/pitcher/serializers/canvasserializers.py#L225
 *
 * Sample PR: https://github.com/PitcherAG/ng-rest-api/pull/1094
 */

import type { MaybeRef } from '@vueuse/core'
import type { Ref } from 'vue'

import type { aspectRatios } from '@canvas-builder/constants'
import type { FontAwesomeType } from '@canvas-builder/types/font-awesome.types'
import type { CollectionPlayer as CollectionPlayerProps } from '@collection-player/types/collection-player.type'
import type { FileModel } from '@lib/components/CFileViewer/CFileViewer.types'
import type { GetFavoritesParams } from '@lib/main.lib'
import type { EmbedLocation, EmbeddedLocationProperties } from '@lib/types/app'
import type { BackgroundSize } from '@lib/types/background'
import type { Call } from '@lib/types/call'
import type { CanvasActions } from '@lib/types/canvasActions'
import type { CanvasThemeAsset, CanvasThemeRetrieve, CanvasTypographyPreset } from '@lib/types/canvasTheme'
import type { CanvasMetadataRetrieve } from '@lib/types/canvases'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { QueryOperator } from '@lib/types/query-builder.types'
import type { RecentFile } from '@lib/types/recentFiles.types'
import type {
  CanvasRetrieve,
  Favorite,
  File,
  FileContentTypeEnum,
  FileRetrieve,
  Folder,
  FolderRetrieve,
  MetadataTemplate,
  PatchedCanvasUpdateRequest,
  SharedLink,
  User,
} from '@openapi'
import type { UiSelectCollectionPlayerRequest, UiSelectCollectionPlayerResponse } from '@sdk/api/modules/ui/types.ui'
import type { OpenExternalUrlRequestPayload, PitcherEnv } from '@sdk/main'
import type { OpenRequestPayload } from '@sdk/payload.types'
type AvailableAspectRatio = (typeof aspectRatios)[number]

export enum HtmlLayoutTypes {
  PREVIEW = 'preview',
  EDIT = 'edit',
}

export enum ContentGridLayoutTypes {
  CAROUSEL = 'carousel',
  GRID = 'grid',
  LIST = 'list',
}

export enum GridLayoutTypes {
  ONE = 'one',
  TWO_EQUAL = 'two-equal',
  TWO_LEFT = 'two-left',
  TWO_RIGHT = 'two-right',
  THREE_EQUAL = 'three-equal',
  FOUR_EQUAL = 'four-equal',
}

export enum LinkAnchorTypes {
  CANVAS_ANCHOR = 'canvas-anchor',
  FILE = 'file',
  WEB = 'web',
}
export enum LinkAlignmentTypes {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum LinkPreviewTypes {
  SOLID_BUTTON = 'solid-button',
  OUTLINE_BUTTON = 'outline-button',
  TEXT_LINK = 'text-link',
}

export interface EmbeddableProps {
  type: 'app'
  app_name: string
  src: string | null
  height?: string
  width?: string
  full_screen_mode?: boolean
  embed_locations?: EmbedLocation[] // legacy, move gradually to properties_by_embed_location
  headless_locations?: EmbedLocation[] // legacy, move gradually to properties_by_embed_location
  properties_by_embed_location?: Partial<Record<EmbedLocation, EmbeddedLocationProperties>>
  file: ContentGridFileProps | null
  settings?: Record<string, unknown>
}

export interface DynamicDatatableProperty {
  key: string
  display_name?: string
  sort: {
    direction?: 'asc' | 'desc'
    default?: boolean
    enabled: boolean
  }
  group?: boolean
  printable?: boolean
  format?: {
    type?: 'currency' | 'number' | 'percentage' | 'date'
    currency?: string
    decimals?: number
    thousandsSeparator?: boolean
    dateFormat?: string
  }
}

export interface DynamicDatatableFilter {
  property: string
  operator: QueryOperator
  value: string
}

export interface DynamicDataTableProps {
  filters?: DynamicDatatableFilter[]
  layout: 'column' | 'row'
  page_size?: number
  path: string
  properties: DynamicDatatableProperty[]
  theme?: {
    background_color?: string | null
    group_header_color?: string | null
    font_family?: string
  }
  break_groups_on_new_page?: boolean
  enable_row_striping?: boolean
  striping_color?: string | null
  striping_start_from_first?: boolean
}

export interface CustomDataTableHierarchyLevel {
  type: 'group' | 'data'
  path: string
  group_by_key?: string
}

export interface CustomDataTableProps {
  hierarchy: CustomDataTableHierarchyLevel[]
  columns: DynamicDatatableProperty[]
  header_path?: string
  footer_path?: string
  filters?: DynamicDatatableFilter[]
  theme?: {
    background_color?: string | null
    group_header_color?: string | null
    font_family?: string
  }
  enable_row_striping?: boolean
  striping_color?: string | null
  striping_start_from_first?: boolean
}

export interface TextProps {
  content?: string
}

export interface BlockOverrides {
  [key: ComponentNode['id']]: { node_data: ComponentNode['data'] }
}

export interface BlockProps {
  id: CanvasRetrieve['id']
  overrides?: BlockOverrides
}

export interface CarouselProps {
  images?: { url: string; is_uploaded_asset?: boolean }[]
  placeholder_height?: string
  include_all_images_in_pdf?: boolean
}

export interface CanvasFile {
  readonly name: FileModel['name']
  readonly file_id: FileModel['id']
  readonly content_extension: FileModel['content_extension']
  readonly content_type: FileModel['content_type']
  readonly content_url: FileModel['content_url']
  readonly thumbnail_url: FileModel['thumbnail_url']
  readonly content_length: FileModel['content_length']
  readonly content_thumbnails: FileModel['content_thumbnails']
  readonly pspdfkit_server_url?: FileModel['pspdfkit_server_url']
  readonly pspdfkit_auth_payload?: FileModel['pspdfkit_auth_payload']
}

const MULTIMEDIA_IMG_TYPE = {
  external: 'external',
  pitcher_asset: 'pitcher_asset',
} as const

export enum MultimediaHorizontalAlignmentOptions {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export type ExternalMultimediaAssetType = {
  asset_type: typeof MULTIMEDIA_IMG_TYPE.external
  external_url: string
  type: 'image' | 'video'
  horizontal_alignment?: MultimediaHorizontalAlignmentOptions
  from?: 'custom' | 'pexels' | 'unsplash' | 'ai'
  poster_url?: string
  poster_background_size?: BackgroundSize
}

export type PitcherMultimediaAssetType = {
  asset_type: typeof MULTIMEDIA_IMG_TYPE.pitcher_asset
  file: {
    id: File['id']
    name: File['name']
    content_url?: File['content_url']
  }
  type: 'image' | 'video'
  horizontal_alignment?: MultimediaHorizontalAlignmentOptions
  poster_url?: string
  poster_background_size?: BackgroundSize
}

export type MultimediaProps = ExternalMultimediaAssetType | PitcherMultimediaAssetType

export type ContentGridCommonFileProps = {
  id: FileRetrieve['id']
  snapshot_id?: FileRetrieve['id']
  name: FileRetrieve['name']
  is_deleted?: boolean
  expires_at?: FileRetrieve['expires_at']
  content_url?: FileRetrieve['content_url']
  content_type?: FileContentTypeEnum | null
  content_extension?: FileRetrieve['content_extension']
  permissions?: FileRetrieve['permissions']
  tags?: FileRetrieve['tags']
}

export type ContentGridFileProps = ContentGridCommonFileProps & {
  thumbnail_url?: FileRetrieve['thumbnail_url']
  content_url: FileRetrieve['content_url']
  content_type: FileContentTypeEnum | null
  content_extension: FileRetrieve['content_extension']
  readonly content_length?: FileRetrieve['content_length']
  content_thumbnails?: FileRetrieve['content_thumbnails'] | null
  readonly pspdfkit_server_url?: FileModel['pspdfkit_server_url']
  readonly pspdfkit_auth_payload?: FileModel['pspdfkit_auth_payload']
}

export type ContentGridItemFileProps = {
  file: ContentGridFileProps
  type: 'file'
}

export type ContentGridItemSlideProps = {
  file: ContentGridCommonFileProps
  slide: {
    index: number
    url?: FileRetrieve['content_thumbnails'][number]
  }
  type: 'slide'
}

export interface ContentGridProps {
  items: (ContentGridItemSlideProps | ContentGridItemFileProps)[]
  size?: 'medium'
  layout_type?: ContentGridLayoutTypes
  data_accessor?: string
}

export interface SectionListSection {
  id: string
  style?: Record<string, any>
  aspect_ratio?: AvailableAspectRatio
  background_video_url?: string
}

export interface SectionListProps {
  sections: SectionListSection[]
  animation_in?: string
  selection_strategy?: 'free' | 'single' | 'at-least-one' | 'app'
  selection_app_name?: string
  is_restricted_to_preselections?: boolean
}

export interface ContentListSection {
  id: string
  file_id?: string
  sequence?: number
  component_id: string
  page_index?: number
  type: 'section' | 'file'
}

export interface ContentListProps {
  sections: ContentListSection[]
  animation_in?: string
  selection_strategy?: 'free' | 'single' | 'at-least-one' | 'app'
  selection_app_name?: string
  is_restricted_to_preselections?: boolean
}

export interface GridItemTooltipProps {
  content?: string
  content_style?: Record<string, any>
  trigger?: string
  trigger_style?: Record<string, any>
}
export interface GridItemProps {
  span: number
  tooltips?: GridItemTooltipProps[]
}

export interface GridProps {
  layout_type: GridLayoutTypes
  columns: number
}

export interface HtmlProps {
  html: string
}

export interface IFrameProps {
  src: string | null
  height?: string
  width?: string
  full_screen_mode?: boolean
  add_session_context?: boolean
}

export type CollectionFileEntity = Pick<
  FileRetrieve,
  | 'id'
  | 'name'
  | 'type'
  | 'tags'
  | 'metadata'
  | 'thumbnail_url'
  | 'permissions'
  | 'content_url'
  | 'content_type'
  | 'pspdfkit_auth_payload'
  | 'pspdfkit_document_id'
  | 'pspdfkit_server_url'
>

export interface LinkProps {
  alignment?: LinkAlignmentTypes
  background_color?: string
  canvas_anchor?: string
  color?: string
  display_text?: string
  file?: (Partial<File> & { page_index?: number }) | null
  font_family?: string
  font_size?: number
  link_type?: LinkAnchorTypes
  page_index?: number
  preview_type?: LinkPreviewTypes
  target?: '_self' | '_blank'
  url?: string
}

export interface ScribbleProps {
  content_background?: {
    file_id?: string
    page_index?: number
    page_id?: string
    is_deleted?: boolean
  }
  asset_background?: {
    asset_id?: string
    is_deleted?: boolean
  }
}

export interface PageBreakProps {}

export interface PageProps {
  aspect_ratio?: AvailableAspectRatio
  background_video_url?: string
}

export interface TimelineEvent {
  top_hint?: string
  top_hint_style?: Record<string, any>
  title?: string
  title_style?: Record<string, any>
  description?: string
  description_style?: Record<string, any>
  image_url?: string
  image_style?: Record<string, any>
  icon?: string
  icon_style?: Record<string, any>
  card_style?: Record<string, any>
}

export interface TimelineProps {
  events: TimelineEvent[]
  layout: 'vertical' | 'horizontal'
}

// ALL NEW COMPONENT PROPS MUST BE ADDED HERE
type AvailableComponentProps =
  | GridProps
  | GridItemProps
  | TextProps
  | CarouselProps
  | MultimediaProps
  | EmbeddableProps
  | ContentGridProps
  | SectionListProps
  | ContentListProps
  | HtmlProps
  | IFrameProps
  | LinkProps
  | PageBreakProps
  | PageProps
  | TimelineProps
  | CollectionPlayerProps
  | DynamicDataTableProps
  | CustomDataTableProps

export interface ComponentNodeVisible {
  edit_mode: boolean
  presentation_mode: boolean
  pdf: boolean
  web: boolean
}

export interface ComponentNodeGenericThemeMeta {
  bg_color_palette_index?: number // theme options background color palette index
}

export interface ComponentNodeDynamicDataTableThemeMeta extends ComponentNodeGenericThemeMeta {
  dt_header_bg_palette_index?: number // dynamic data table component header background palette index
  dt_group_header_bg_palette_index?: number // dynamic data table component group header background palette index
}

export interface ComponentNodeMultimediaThemeMeta extends ComponentNodeGenericThemeMeta {
  is_theme_logo?: boolean // indicates if this component is using the theme's logo
  theme_asset_index?: number // index of the theme asset this component is using
}

export type ComponentNodeThemeMeta =
  | ComponentNodeGenericThemeMeta
  | ComponentNodeDynamicDataTableThemeMeta
  | ComponentNodeMultimediaThemeMeta

export interface ComponentNodeLinkable {
  linkable_in_templates: boolean
}

export interface ComponentNodePrint {
  scale_to_fit_page?: boolean
}

export interface ComponentNode<T = AvailableComponentProps> {
  id: string
  type: ComponentTypes
  tracking_id: string
  visible?: ComponentNodeVisible
  linkable?: ComponentNodeLinkable
  print?: ComponentNodePrint
  allow_admins_to_overwrite?: boolean
  autofill?: 'off' | 'ai' | 'matching_metadata' | 'matching_tags' | 'matching_metadata_or_tags'
  autofill_content_types?: FileContentTypeEnum[] | null
  data?: T
  children?: ComponentNode[]
  when_used_in_section?: {
    is_editable: boolean // can change the component's data and add children
    is_removable: boolean // can remove the component
  }
  style?: Record<string, any>
  tags?: string[]
  theme_meta?: ComponentNodeThemeMeta
  height?: string
  is_sharebox_target?: boolean // marks this ContentGrid as the target for sharebox items when creating a canvas from sharebox
}

export interface HomePageConfig {
  component_id: string
  section_id?: string | null
}

export interface CanvasContent {
  data?: ComponentNode[]
  overrides?: SectionComponentOverrides
  home_page?: HomePageConfig
  hidden?: {
    dsr: {
      sections: HiddenElementsInCanvas
      components: HiddenElementsInCanvas
    }
    dsr_print: {
      sections: HiddenElementsInCanvas
      components: HiddenElementsInCanvas
    }
  }
}

export interface HiddenElementsInCanvas {
  [elementId: string]: true
}

export type SectionComponentOverride = {
  node?: ComponentNode
  after_sibling_id?: ComponentNode['id'] // only exists when the key is 'root'
  is_removed?: true
}

export type SectionOverrides = {
  [componentId: ComponentNode['id'] | 'root']: SectionComponentOverride
} & {
  modified_by?: { id: User['id'] }
  modified_at?: string
}

export type SectionComponentOverrides = {
  [sectionId: CanvasRetrieve['id']]: SectionOverrides
}

export interface ComponentMeta {
  allowedChildren: ComponentTypes[]
  defaultChildren: Partial<ComponentNode>[]
  defaultData: AvailableComponentProps
  singleton?: boolean // can be added only once per canvas; default: false
  defaultPrintable: boolean
}

export enum ComponentTypes {
  Block = 'Block',
  Carousel = 'Carousel',
  CollectionPlayer = 'CollectionPlayer',
  ContentGrid = 'ContentGrid',
  CustomDataTable = 'CustomDataTable',
  DataCharts = 'DataCharts',
  DynamicDataTable = 'DynamicDataTable',
  Embeddable = 'Embeddable',
  Grid = 'Grid',
  GridItem = 'GridItem',
  Html = 'Html',
  IFrame = 'IFrame',
  Link = 'Link',
  Multimedia = 'Multimedia',
  Page = 'Page',
  PageBreak = 'PageBreak',
  Root = 'Root',
  Scribble = 'Scribble',
  SectionList = 'SectionList',
  ContentList = 'ContentList',
  Text = 'Text',
  Timeline = 'Timeline',
}

export const ComponentIcon = {
  AR: 'cube',
  Block: 'objects-column',
  Carousel: 'gallery-thumbnails',
  Collection: 'rectangle-history-circle-plus',
  CollectionPlayer: 'regular-rectangle-history-circle-play',
  ContentGrid: 'files',
  CustomDataTable: 'table-tree',
  DataCharts: 'chart-line',
  DynamicDataTable: 'regular-table-circle-bolt',
  Embeddable: 'browser',
  File: 'file-circle-plus',
  Grid: 'table',
  GridItem: 'objects-column',
  GridParent: 'angle-down',
  Html: 'code',
  IFrame: 'window',
  Link: 'hand-pointer',
  Multimedia: 'image',
  Page: 'pager',
  PageBreak: 'arrows-from-line',
  Scribble: 'pen-swirl',
  SectionList: 'bring-forward',
  ContentList: 'images',
  Text: 'text-size',
  Timeline: 'timeline-arrow',
  Video: 'video',
} as const

export const componentIconType: Record<string, FontAwesomeType> = {
  Block: 'fas',
  CollectionPlayer: 'fa-kit',
  DataCharts: 'fas',
  DynamicDataTable: 'fa-kit',
}

export interface CanvasPdfDimensions {
  preset?: 'A4' | 'A3' | 'A5' | 'B3' | 'B4' | 'B5' | 'US Legal' | 'US Letter' | 'Custom'
  width?: string // e.g., "210mm", "8.5in"
  height?: string // e.g., "297mm", "11in"
}

export type CanvasTheme = {
  background_video_url?: string
  canvas_bg_color?: string
  global_component_spacing?: number
  font?: { name: string; url: string }
  fluid?: boolean
  palette?: string[]
  style?: Record<string, any>
  typography?: Record<string, CanvasTypographyPreset>
  aspect_ratio?: AvailableAspectRatio
  maintain_section_theme?: boolean
  inherit_theme_from_parent?: boolean
  logo?: CanvasThemeAsset | null
  assets?: CanvasThemeAsset[]
  pdf_dimensions?: CanvasPdfDimensions
}

export enum CanvasBuilderMode {
  ADMIN = 'admin',
  IMPACT = 'impact',
  SHARING = 'sharing',
  HOME = 'home',
}

export interface ComponentTrackingParams {
  trackable: boolean
}

export enum CanvasHistoryAction {
  ADD = 'add',
  REMOVE = 'remove',
  REORDER = 'reorder',
  OPEN = 'open',
  SAVE = 'save',
}

export interface CanvasHistoryItem {
  active?: boolean
  action: CanvasHistoryAction
  componentType: ComponentTypes
  icon?: string
  id: string
  snapshot: ComponentNode[]
  timestamp: string
}

export interface FileProps {
  file_id: FileRetrieve['id']
  source_file_id?: FileRetrieve['id']
  name: FileRetrieve['name']
  thumbnail_url?: FileRetrieve['thumbnail_url']
  content_url: FileRetrieve['content_url']
  content_type: FileRetrieve['content_type']
  content_extension: FileRetrieve['content_extension']
  content_length: FileRetrieve['content_length']
  content_thumbnails: FileRetrieve['content_thumbnails'] | null
  is_deleted?: boolean
  readonly pspdfkit_server_url?: FileModel['pspdfkit_server_url']
  readonly pspdfkit_auth_payload?: FileModel['pspdfkit_auth_payload']
}

export type FileOpenParams =
  | (FileProps & {
      page_index?: number
      newTab?: boolean
      files?: OpenRequestPayload['files']
      used_in_section_id?: CanvasRetrieve['id']
    })
  | {
      file_id: File['id']
      name?: File['name']
      page_index?: number
      newTab?: boolean
      files?: OpenRequestPayload['files']
      used_in_section_id?: CanvasRetrieve['id']
    }

type NoCRMBuilderProps = {
  isCRM: false
  onSfdcAccountSearch?: never
}

export type NodeInfo = {
  id?: string
  type: ComponentTypes
}

export type SharingBuilderProps = NoCRMBuilderProps & {
  selectCollectionPlayerContent?: never
  isFullscreen?: boolean
  displayMode?: 'default' | 'print'
  startPage?: number
  endPage?: number
  isRedirectEvent?: never
  mode: CanvasBuilderMode.SHARING
  metadata: {
    baseStackingContext?: number
  }
  activeCanvas: CanvasRetrieve
  onExit: () => void
  onSave?: never
  onEnter?: () => void
  onPageEnter?: (data: ComponentNode[], pageIndex: number) => void
  onPageExit?: (data: ComponentNode[], pageIndex: number) => void
  onWizardEnter?: never
  onWizardExit?: never
  onFileEnter?: (node: NodeInfo, file: CollectionFileEntity) => void
  onFileExit?: (node: NodeInfo, file: CollectionFileEntity) => void
  onFilePageEnter?: (node: NodeInfo, file: CollectionFileEntity, pageIndex: number) => void
  onFilePageExit?: (node: NodeInfo, file: CollectionFileEntity, pageIndex: number) => void
  onCollectionPlayerContentChange?: never
  onComponentEnter?: (data: ComponentNode | CanvasRetrieve, params: ComponentTrackingParams) => void
  onComponentExit?: (data: ComponentNode | CanvasRetrieve, params: ComponentTrackingParams) => void
  onAppEnter?: (data: { src: string }) => void
  onAppExit?: (data: { src: string }) => void
  onFileOpen: (file: FileOpenParams) => void
  onOpenExternalUrl: (payload: OpenExternalUrlRequestPayload) => Promise<void>
  deleteFile?: never
  listFiles?: never
  listRecommendedFiles?: never
  listCanvases?: never
  fetchFolder?: never
  fetchFile: (fileId: FileRetrieve['id']) => Promise<FileRetrieve>
  fetchFavorites?: never
  fetchRecentFiles?: never
  fetchInstanceMetadataTemplate?: never
  fetchCanvas?: (canvasId: CanvasRetrieve['id'], params?: Record<string, any>) => Promise<CanvasRetrieve>
  renderPageAsImageUrl: (payload: {
    fileId: File['id']
    documentId?: File['id']
    pageIndex: number
    params: { width: number } | { height: number }
  }) => Promise<ArrayBuffer>
  getEnv: () => Promise<PitcherEnv>
  onTrackShare?: never
  share?: never
  onEditMode: never
  inCall?: never
  myUserId?: never
  onDownloadFile?: (file: File | ContentGridFileProps | ContentGridCommonFileProps) => Promise<void>
  onDownloadCanvas?: (canvas: CanvasRetrieve, fileFormat?: 'pdf' | 'pptx') => Promise<void>
  theme: CanvasThemeRetrieve | null
  isAnyTypeOfAdmin: boolean
  canvasMetadata: CanvasMetadataRetrieve | null
}

export interface CRMBuilderProps {
  isCRM: true
  onSfdcAccountSearch: (searchTerm?: MaybeRef<string>) => {
    accountsLoading: Ref<boolean>
    accounts: Ref<
      {
        label: string
        value: string
      }[]
    >
  }
}
type AdminImpactCommonBuilderProps = {
  selectCollectionPlayerContent: (
    payload?: UiSelectCollectionPlayerRequest,
  ) => Promise<UiSelectCollectionPlayerResponse>
  openOnStart?: {
    component?: string
    section?: string
  }
  isFullscreen?: boolean
  displayMode?: 'default' | 'print'
  startPage?: number
  endPage?: number
  metadata: {
    baseStackingContext?: number
    startInMode?: CanvasActions.Present | CanvasActions.Edit
  }
  activeCanvas: CanvasRetrieve
  onExit: () => void
  onEnter?: () => void
  onComponentEnter?: (data: ComponentNode | CanvasRetrieve, params: ComponentTrackingParams) => void
  onComponentExit?: (data: ComponentNode | CanvasRetrieve, params: ComponentTrackingParams) => void
  onAppEnter?: (data: { src: string }) => void
  onAppExit?: (data: { src: string }) => void
  onWizardEnter?: () => void
  onWizardExit?: () => void
  onPageEnter?: (data: ComponentNode[], pageIndex: number) => void
  onPageExit?: (data: ComponentNode[], pageIndex: number) => void
  onFileEnter?: (node: NodeInfo, file: CollectionFileEntity) => void
  onFileExit?: (node: NodeInfo, file: CollectionFileEntity) => void
  onFilePageEnter?: (node: NodeInfo, file: CollectionFileEntity, pageIndex: number) => void
  onFilePageExit?: (node: NodeInfo, file: CollectionFileEntity, pageIndex: number) => void
  onCollectionPlayerContentChange?: (payload: {
    componentId: string
    uniqueFilesCount: number
    groupsCount: number
    slidesCount: number
  }) => void
  onSave: (
    canvasId: CanvasRetrieve['id'],
    canvas: PatchedCanvasUpdateRequest & { fields?: string; include_expired_files?: boolean },
    showToast?: boolean,
  ) => Promise<CanvasRetrieve>
  onFileOpen: (file: FileOpenParams) => void
  deleteFile: (fileId: FileRetrieve['id']) => Promise<string | void>
  listFiles: (params?: any) => Promise<File[]>
  listCanvases: (params?: any) => Promise<PaginatedData<CanvasRetrieve>>
  fetchFolder: (folderId: Folder['id'] | undefined) => Promise<FolderRetrieve>
  fetchFile: (fileId: FileRetrieve['id']) => Promise<FileRetrieve>
  fetchFavorites: (payload?: GetFavoritesParams) => Promise<PaginatedData<Favorite>>
  fetchRecentFiles: () => Promise<RecentFile[]>
  fetchCanvas: (canvasId: CanvasRetrieve['id'], params?: Record<string, any>) => Promise<CanvasRetrieve>
  fetchInstanceMetadataTemplate: () => Promise<MetadataTemplate>
  renderPageAsImageUrl: (payload: {
    fileId: File['id']
    documentId?: File['id']
    pageIndex: number
    params: { width: number } | { height: number }
  }) => Promise<ArrayBuffer>
  getEnv?: () => Promise<PitcherEnv>
  onEditMode?: (isEditMode: boolean) => void
  onTrackShare?: (canvasId: CanvasRetrieve['id']) => Promise<void>
  onOpenExternalUrl: (payload: OpenExternalUrlRequestPayload) => Promise<void>
  share?: (canvasId: CanvasRetrieve['id']) => Promise<SharedLink>
  isCRM: boolean
  onDownloadFile?: (file: File | ContentGridFileProps | ContentGridCommonFileProps) => Promise<void>
  onDownloadCanvas?: (canvas: CanvasRetrieve, fileFormat?: 'pdf' | 'pptx') => Promise<void>
  theme: CanvasThemeRetrieve | null
  isAnyTypeOfAdmin: boolean
  canvasMetadata: CanvasMetadataRetrieve | null
  hideEdit?: boolean
}

type AdminBuilderProps = AdminImpactCommonBuilderProps &
  (CRMBuilderProps | NoCRMBuilderProps) & {
    isRedirectEvent?: never
    mode: CanvasBuilderMode.ADMIN
    inCall?: never
    myUserId?: never
    listRecommendedFiles?: never
  }

type ImpactBuilderProps = AdminImpactCommonBuilderProps &
  (CRMBuilderProps | NoCRMBuilderProps) & {
    isRedirectEvent?: boolean
    mode: CanvasBuilderMode.IMPACT
    inCall?: boolean
    call?: Call
    myUserId?: User['id']
    listRecommendedFiles: (params: {
      sectionId: CanvasRetrieve['id']
      matchingStrategy: ('tags' | 'metadata')[]
    }) => Promise<File[]>
  }

type HomeBuilderProps = AdminImpactCommonBuilderProps &
  (CRMBuilderProps | NoCRMBuilderProps) & {
    isRedirectEvent?: boolean
    mode: CanvasBuilderMode.HOME
    inCall?: boolean
    call?: Call
    myUserId?: User['id']
    listRecommendedFiles: (params: {
      sectionId: CanvasRetrieve['id']
      matchingStrategy: ('tags' | 'metadata')[]
    }) => Promise<File[]>
  }

export type CanvasBuilderProps = AdminBuilderProps | ImpactBuilderProps | SharingBuilderProps | HomeBuilderProps
